import type { CustomerioIdentifyAttributes } from "shared/components/Messenger/customerioIdentify";

/** Customer.io JS SDK expects snake_case attribute keys */
export type CustomerioSdkIdentifyPayload = {
  id: string;
  email: string;
  name: string;
  role: string;
  company_slug: string;
  company_name: string;
  /** Unix timestamp in seconds (user signup time) */
  created_at: number;
  user_hash: string;
};

export const CUSTOMERIO_TRACK_SCRIPT_SRC =
  "https://assets.customer.io/assets/track.js";

export const CUSTOMERIO_TRACKER_SCRIPT_ID = "cio-tracker";

/** True after Customer.io track.js has replaced the queue shim. */
export const isCustomerioTrackLoaded = (): boolean => {
  const cio = window._cio;
  return Boolean(
    cio && !Array.isArray(cio) && typeof cio.identify === "function"
  );
};

/** True once track.js fired `load` (data-cio-loaded) or the SDK replaced the queue shim. */
export const isCustomerioSnippetScriptReady = (): boolean => {
  const el = document.getElementById(
    CUSTOMERIO_TRACKER_SCRIPT_ID
  ) as HTMLScriptElement | null;
  if (!el) return false;
  if (el.getAttribute("data-cio-error") === "true") return false;
  if (el.getAttribute("data-cio-loaded") === "true") return true;
  return isCustomerioTrackLoaded();
};

export const buildCustomerioIdentifyFingerprint = (
  attrs: Pick<
    CustomerioIdentifyAttributes,
    "id" | "companySlug" | "role" | "email"
  >
): string =>
  [attrs.id, attrs.companySlug ?? "", attrs.role ?? "", attrs.email].join("|");

let lastCustomerioIdentifyFingerprint: string | null = null;

export const getLastCustomerioIdentifyFingerprint = (): string | null =>
  lastCustomerioIdentifyFingerprint;

export const setLastCustomerioIdentifyFingerprint = (
  fingerprint: string | null
): void => {
  lastCustomerioIdentifyFingerprint = fingerprint;
};

export const toCustomerioSdkIdentifyPayload = (
  attrs: CustomerioIdentifyAttributes
): CustomerioSdkIdentifyPayload => ({
  id: attrs.id,
  email: attrs.email,
  name: attrs.name,
  role: attrs.role,
  company_slug: attrs.companySlug,
  company_name: attrs.companyName,
  created_at: attrs.createdAt,
  user_hash: attrs.userHash,
});

export const identifyCustomerio = (
  attrs: CustomerioIdentifyAttributes
): void => {
  const cio = window._cio;
  if (!cio || typeof cio.identify !== "function") return;

  cio.identify(toCustomerioSdkIdentifyPayload(attrs));
};

export const resetCustomerIO = (): void => {
  lastCustomerioIdentifyFingerprint = null;
  try {
    const cio = window._cio;
    if (!cio) return;

    if (typeof cio.reset === "function") {
      cio.reset();
    }
  } catch {
    // ignore — logout must not break the app
  }
};
